import { useAuth } from '@/contexts/AuthContext';
import Api from '@/services/Api';
import { SyncService } from '@/services/Sync';
import AsyncStorage from '@/platform/async-storage';
import iconPng from '@/assets/images/icon.png';
import { useEffect, useMemo, useState } from 'react';

/** Logo padrão exibida quando a empresa não tem imagem ou ainda não foi resolvida. */
// WEB: asset importado vira URL string (aceita direto pelo `source` do <Image> de @/ui/Image).
export const DEFAULT_EMPRESA_LOGO: string = iconPng;

/** Cache local persistido: mapa hash-da-empresa → string da logo (base64 ou URL). */
const EMPRESA_LOGOS_CACHE_KEY = '@tirvumais:empresaLogos';

/** Resolve a logo da empresa (base64 ou URL) com fallback para o ícone local. */
export const resolveEmpresaLogoSource = (logo?: string | null) => {
  const value = String(logo || '').trim();
  if (!value) return DEFAULT_EMPRESA_LOGO;
  if (value.startsWith('data:image')) return { uri: value };
  if (/^https?:\/\//i.test(value)) return { uri: value };
  // base64 "cru" (sem prefixo data:) — trata como PNG.
  if (value.length > 100 && /^[A-Za-z0-9+/=\s]+$/.test(value)) {
    return { uri: `data:image/png;base64,${value.replace(/\s/g, '')}` };
  }
  return DEFAULT_EMPRESA_LOGO;
};

/** Extrai a logo de um item de empresa, na mesma ordem de prioridade do switcher. */
const extractLogo = (item: any): string | null =>
  item?.img_icon ?? item?.img_logo ?? item?.img_logo_horizontal ?? null;

const readCache = async (): Promise<Record<string, string>> => {
  try {
    const raw = await AsyncStorage.getItem(EMPRESA_LOGOS_CACHE_KEY);
    const parsed = raw ? JSON.parse(raw) : null;
    return parsed && typeof parsed === 'object' ? parsed : {};
  } catch {
    return {};
  }
};

const writeCache = async (map: Record<string, string>) => {
  try {
    await AsyncStorage.setItem(EMPRESA_LOGOS_CACHE_KEY, JSON.stringify(map));
  } catch {
    /* cache é best-effort */
  }
};

/** Persiste a logo de uma empresa específica (usado no fluxo de login). */
export const cacheEmpresaLogo = async (hash?: string | null, logo?: string | null) => {
  const h = String(hash || '').trim();
  const l = String(logo || '').trim();
  if (!h || !l) return;
  const map = await readCache();
  if (map[h] === l) return;
  map[h] = l;
  await writeCache(map);
};

/** Persiste as logos de uma lista de empresas (usado ao abrir o switcher). */
export const cacheEmpresaLogosFromList = async (payload: any) => {
  const list: any[] = Array.isArray(payload)
    ? payload
    : Array.isArray(payload?.data)
      ? payload.data
      : Array.isArray(payload?.empresas)
        ? payload.empresas
        : payload
          ? [payload]
          : [];
  if (list.length === 0) return;

  const map = await readCache();
  let changed = false;
  for (const item of list) {
    const h = String(item?.hash ?? item?.Hash ?? '').trim();
    const l = extractLogo(item);
    if (h && l && map[h] !== l) {
      map[h] = l;
      changed = true;
    }
  }
  if (changed) await writeCache(map);
};

/**
 * Resolve a logo da empresa atualmente selecionada (identificada por `userData.hash`).
 * Ordem de resolução: logo embutida no userData → cache local → busca em
 * Login/listarEmpresas (somente no cache-miss e com internet). Ao trocar de empresa,
 * o `userData.hash` muda e a logo é re-resolvida automaticamente.
 */
export function useEmpresaLogo() {
  const { userData } = useAuth();
  const hash = String((userData as any)?.hash ?? '').trim();
  const cpf = String((userData as any)?.cpf ?? '').trim();
  // Logo que eventualmente já venha embutida no próprio userData (iniciar/login).
  const inlineLogo =
    (userData as any)?.img_icon ??
    (userData as any)?.img_logo ??
    (userData as any)?.img_logo_horizontal ??
    (userData as any)?.done?.img_icon ??
    (userData as any)?.colaborador?.img_icon ??
    null;

  const [logo, setLogo] = useState<string | null>(inlineLogo ? String(inlineLogo) : null);

  useEffect(() => {
    let cancelled = false;

    if (!hash) {
      setLogo(null);
      return;
    }

    // 1) Logo embutida no userData tem prioridade.
    if (inlineLogo) {
      const value = String(inlineLogo);
      setLogo(value);
      void cacheEmpresaLogo(hash, value);
      return;
    }

    (async () => {
      // 2) Cache local → render instantâneo.
      const map = await readCache();
      if (cancelled) return;
      if (map[hash]) {
        setLogo(map[hash]);
        return;
      }
      // 3) Cache-miss → busca a lista (precisa de CPF + internet) e cacheia.
      if (!cpf) return;
      try {
        const online = await SyncService.checkInternet();
        if (!online || cancelled) return;
        const resp = await Api.autenticacao.listarEmpresas(cpf);
        if (cancelled || !resp?.success) return;
        await cacheEmpresaLogosFromList(resp.done);
        const next = await readCache();
        if (!cancelled && next[hash]) setLogo(next[hash]);
      } catch {
        /* mantém o fallback */
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [hash, cpf, inlineLogo]);

  // Memoizado: sem isso, CADA render do PageFragment (header de todas as telas) criava um
  // objeto novo {uri: base64} com a logo inteira (centenas de KB) — identidade nova por render.
  return useMemo(() => resolveEmpresaLogoSource(logo), [logo]);
}
